<template>
  <div
    class="relative max-h-[80vh] min-h-78 space-y-9"
    data-cy="stips-container"
  >
    <!-- Header -->
    <div v-if="!viewOnly" class="flex w-full flex-col space-y-5">
      <div
        class="sticky top-0 z-1 flex bg-white pb-4"
        :class="hideTitle ? 'justify-end' : 'justify-between'"
      >
        <lf-h1 v-if="!hideTitle">
          {{ $t("DEALS.DEAL_PROGRESS.EDIT_REQ_STIPS") }}
        </lf-h1>
        <div class="flex shrink-0 justify-end space-x-2 lg:col-span-2">
          <outline-button
            :disabled="!selectedStips.length || viewOnly"
            data-cy="unsellect-all-stips"
            @click="selectedStips = []"
          >
            {{ `${$t("COMMON.UNSELECT")}  ${$t("COMMON.ALL")}` }}
          </outline-button>
          <lf-button
            v-if="canAddStips && !viewOnly"
            :disabled="isLoading"
            class="bg-primary/10 font-semibold text-primary"
            data-cy="createNewStipButton"
            @click="onNewStipClick"
          >
            {{ $t("STIPS.NEW_STIP") }}
          </lf-button>
        </div>
      </div>
      <search-input v-model="search" class="h-11" no-margin no-padding />
    </div>

    <!-- Stips -->
    <loader :is-loading="isLoading" />
    <div
      ref="stipsContainer"
      class="stips-list-wrapper min-h-78 overflow-y-auto"
      data-cy="stip-list"
    >
      <stip
        v-for="(stip, index) in stipsList"
        :key="stip.id || index"
        :stip
        :select-only
        :selected="stipIdToSelectedStipMap.has(stip.id)"
        :disabled="viewOnly"
        class="max-h-16"
        @stip:update="updateStip"
        @stip:checkbox-update="changeSelectedStips(stip, $event)"
      />
      <div class="relative">
        <form
          v-if="isCreatingStip"
          class="absolute max-h-58 w-full rounded-sm border border-divider bg-white p-3"
          @submit="submitNewStip"
        >
          <div class="mb-3 flex w-full flex-1 justify-between px-1">
            <button
              class="cursor-pointer font-medium text-gray-400 focus:outline-hidden"
              @click="isCreatingStip = false"
            >
              {{ $t("COMMON.CANCEL") }}
            </button>
            <button
              type="submit"
              class="cursor-pointer font-semibold text-primary focus:outline-hidden"
            >
              {{ $t("COMMON.SAVE") }}
            </button>
          </div>
          <lf-input
            :placeholder="$t('STIPS.STIP_NAME')"
            :value="newStip.name"
            name="name"
            type="text"
            class="text-xs"
            no-margin
            ref="newStipNameRef"
          />
          <lf-input
            :placeholder="$t('STIPS.ADD_DESCRIPTION')"
            name="description"
            :value="newStip.description"
            no-margin
            class="mt-1-5"
            :maxlength="235"
          />
          <stip-dropdown-categories
            name="file_type"
            class="mt-3"
            :placeholder="$t('STIPS.SELECT_FILE_TYPE')"
            dropdown-teleported
            v-model="selectedDocumentType"
          />
        </form>
        <button
          v-else-if="canAddStips && !selectOnly && !viewOnly"
          class="flex h-13 w-full items-center justify-center space-x-3 rounded-sm border border-divider p-3 hover:bg-gray-50 focus:outline-hidden"
          @click="onNewStipClick"
        >
          <span class="font-semibold">
            {{ $t("DEALS.DEAL_PROGRESS.ADD_NEW_STIP") }}
          </span>
          <icon-base :icon="IconPlusSmall" class="text-secondary" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useInfiniteScroll, watchImmediate } from "@vueuse/core";
import { computed, nextTick, reactive, ref } from "vue";

import uniqBy from "lodash/uniqBy";
import { useField, useForm } from "vee-validate";

import { useAuth } from "@/hooks/auth";
import { useStipsInfiniteQuery } from "@/hooks/queries/stips";
import { useCreateStipMutation } from "@/hooks/queries/stips";
import { isDealStip } from "@/models/stips";
import stipsApiService from "@/services/modules/stips";

import StipDropdownCategories from "@/components/deals/StipDropdownCategories.vue";
import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Loader from "@/components/ui/Loader.vue";
import LfButton from "@/components/ui/buttons/LfButton.vue";
import OutlineButton from "@/components/ui/buttons/OutlineButton.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import Stip from "@/views/deals/components/Stip.vue";

import type { DealStip, FieldStip, StipDefinition } from "@/models/stips";

type UnifiedStip = StipDefinition | DealStip;

const {
  hideTitle = false,
  requiredStips = [],
  preselectStips = [],
  viewOnly = false,
  selectOnly = false,
} = defineProps<{
  hideTitle?: boolean;
  requiredStips?: UnifiedStip[];
  preselectStips?: (number | string)[];
  requiredStipsFirst?: boolean;
  viewOnly?: boolean;
  selectOnly?: boolean;
}>();

const selectedDocumentType = ref();
const { isAdmin, isClientAdmin, isClientUnderwriter } = useAuth();

const stipsContainer = ref<HTMLDivElement | null>(null);

const search = ref("");
const filtersAsSearchParams = computed(() => ({ search: search.value }));

const newStipNameRef = ref<InstanceType<typeof LfInput> | null>(null);
const isCreatingStip = ref(false);
const newStip = reactive({
  name: "",
  description: "",
});

const {
  data: stipsData,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
} = useStipsInfiniteQuery(filtersAsSearchParams);

const { mutateAsync: createStip } = useCreateStipMutation();

useInfiniteScroll(
  stipsContainer,
  async () => {
    await fetchNextPage();
  },
  {
    distance: 100,
    canLoadMore: () => {
      return !isLoading.value && !isFetchingNextPage.value;
    },
  }
);

const preselectStipsDefinitions = ref<StipDefinition[]>([]);

const stipsList = computed<FieldStip[]>(() => {
  const mergedStipDefinitions = uniqBy(
    [
      ...preselectStipsDefinitions.value,
      ...(stipsData.value?.pages.flatMap((page) => page.data) ?? []),
    ],
    "id"
  );

  return mergedStipDefinitions.map((stip) => {
    const selectedStip = stipIdToSelectedStipMap.value.get(stip.id);

    return {
      ...stip,
      count: selectedStip?.count ?? 1,
      description: selectedStip?.description ?? stip.description,
    };
  });
});

const stipIdToSelectedStipMap = computed(() => {
  const map = new Map<number | string, (typeof selectedStips.value)[number]>();
  selectedStips.value.forEach((stip) => {
    map.set(stip.id, stip);
  });
  return map;
});

const canAddStips = computed(
  () => isAdmin || isClientAdmin || isClientUnderwriter
);

const getStipId = (stip: UnifiedStip | FieldStip) => {
  if (isDealStip(stip)) {
    return stip.stip_id;
  }

  return stip.id;
};

const toFieldStip = (stip: UnifiedStip | FieldStip): FieldStip => {
  return {
    id: getStipId(stip),
    name: stip.name,
    description: stip.description,
    file_type: stip.file_type,
    count: isDealStip(stip) ? stip.count : 1,
  };
};

const getFieldValues = (stips: UnifiedStip[]): FieldStip[] => {
  return stips.map(toFieldStip);
};

const { value: selectedStips } = useField<FieldStip[]>("stips", undefined, {
  initialValue: getFieldValues(
    requiredStips.map((stip) => ({
      ...stip,
      id: getStipId(stip),
      count: isDealStip(stip) ? stip.count : 1,
    }))
  ),
});

const updateStip = ({ id, description, count }: FieldStip) => {
  const index = selectedStips.value.findIndex((stip) => stip.id === id);

  if (index === -1) {
    // If stip is not selected, add it to selectedStips
    const stip = stipsList.value.find((s) => s.id === id);
    if (stip) {
      selectedStips.value.push(toFieldStip({ ...stip, description, count }));
    }
    return;
  }

  selectedStips.value = selectedStips.value.map((stip, i) =>
    i === index ? { ...stip, description, count } : stip
  );
};

const changeSelectedStips = (stip: FieldStip, shouldAdd: boolean) => {
  if (shouldAdd) {
    selectedStips.value.push(toFieldStip(stip));
  } else {
    selectedStips.value = selectedStips.value.filter((s) => s.id !== stip.id);
  }
};

const { handleSubmit: handleSubmitNewStip } = useForm({
  initialValues: newStip,
});

const submitNewStip = handleSubmitNewStip(async (values) => {
  const createdStip = await createStip({
    name: values.name,
    description: values.description,
    file_type: selectedDocumentType.value,
  });

  selectedStips.value.push(toFieldStip(createdStip));
  isCreatingStip.value = false;
});

const externalOnSubmit = async () => {
  if (isCreatingStip.value) {
    await submitNewStip();
  }
  return selectedStips.value.map((fieldStip) => ({
    id: fieldStip.id,
    description: fieldStip.description,
    file_type: fieldStip.file_type,
    count: fieldStip.count,
  }));
};

const onNewStipClick = async () => {
  isCreatingStip.value = true;
  await nextTick();
  newStipNameRef.value?.$el?.querySelector("input")?.focus();
};

const setupPreselectedStips = async () => {
  if (!preselectStips?.length) {
    preselectStipsDefinitions.value = [];
    return;
  }

  preselectStipsDefinitions.value = (
    await stipsApiService.getStips({ ids: preselectStips })
  ).data;

  if (!preselectStipsDefinitions.value.length) {
    return;
  }

  selectedStips.value = uniqBy(
    [
      ...selectedStips.value,
      ...preselectStipsDefinitions.value.map(toFieldStip),
    ],
    "id"
  );
};

watchImmediate(() => preselectStips.length, setupPreselectedStips);

defineExpose({ externalOnSubmit });
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.stips-list-wrapper {
  @apply relative grid grid-cols-1 justify-around gap-3 md:grid-cols-2 lg:grid-cols-3;
  max-height: calc(70vh - 200px);
}
</style>
